import { Link } from "react-router-dom";
import { useRef, useEffect } from "react";
import { ArrowUpRight } from "lucide-react";
import { JASON_PORTRAIT } from "../lib/content";
import { Reveal, MaskText, Line } from "../components/Motion";

export const AboutPreview = () => {
  const ref = useRef(null);
  const imgRef = useRef(null);

  // Subtle portrait parallax (scroll listener replaces the animation library)
  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const el = ref.current;
        const img = imgRef.current;
        if (el && img) {
          const r = el.getBoundingClientRect();
          const total = r.height + window.innerHeight;
          const progress = Math.min(Math.max((window.innerHeight - r.top) / total, 0), 1);
          img.style.transform = `translateY(${(-8 + progress * 16).toFixed(2)}%) scale(1.12)`;
        }
        ticking = false;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <section ref={ref} data-testid="about-preview" className="relative bg-ink text-paper py-24 md:py-40 overflow-hidden">
      <div className="max-w-[1500px] mx-auto px-5 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20 items-center">
          <div className="md:col-span-5">
            <div className="relative overflow-hidden aspect-[4/5] bg-paper/5">
              <img ref={imgRef} src={JASON_PORTRAIT} alt="Jason Howard, wedding photographer based in Chester" className="img-cover" loading="lazy" style={{ transform: "translateY(-8%) scale(1.12)" }} />
            </div>
          </div>

          <div className="md:col-span-7">
            <Reveal><p className="text-sand text-[11px] uppercase tracking-[0.3em] mb-6">Hello, I'm Jason</p></Reveal>
            <MaskText as="h2" className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[0.95] mb-8" lines={["( but everyone", "calls me J )"]} />
            <Reveal delay={0.1}>
              <p className="text-paper/75 text-base md:text-lg leading-relaxed max-w-xl">
                I've been a wedding and portrait photographer in Chester for over twenty years.
                My style is relaxed and unobtrusive — I'd rather you feel you've invited an old
                friend who happens to have a really nice camera. Every wedding is unique, so it's
                all about getting to know each other and going on the journey together.
              </p>
            </Reveal>

            <Line className="!bg-paper/15 my-10" delay={0.2} />

            <Reveal delay={0.15}>
              <div className="bg-paper/[0.04] border border-paper/10 rounded-sm p-7 md:p-9">
                <p className="text-sand text-[10px] uppercase tracking-[0.3em] mb-4">Founder — Echo Ridge Retreat</p>
                <p className="text-paper/75 leading-relaxed">
                  Beyond weddings, I'm a former Royal Marine building{" "}
                  <span className="text-paper">Echo Ridge Retreat</span> — a peaceful countryside
                  sanctuary for veterans, serving personnel and blue-light workers.
                  Somewhere people can breathe, feel safe and begin to reconnect through nature,
                  photography and shared experience.
                </p>
                <a
                  href="https://echoridgeretreat.co.uk"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="echo-ridge-link"
                  className="inline-flex items-center gap-2 mt-6 text-sm text-paper link-underline"
                >
                  Discover Echo Ridge Retreat <ArrowUpRight size={16} />
                </a>
              </div>
            </Reveal>

            <Reveal delay={0.2}>
              <Link to="/about/" data-testid="about-read-more" className="inline-flex items-center gap-2 mt-10 text-[11px] uppercase tracking-[0.22em] link-underline">
                More about me <ArrowUpRight size={15} />
              </Link>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
